import DateBadge from '../DateBadge';
import useTaskSubmitAction from '../hooks/useTaskSubmitAction';
import { Task, ACTIVE_TASK_STATUSES } from '../types';
import getTaskDisplay from '../utils/getTaskDisplay';
import isOverlaySubmitAction from '../utils/isOverlaySubmitAction';
import priorityToDateBadgeIcon from '../utils/priorityToDateBadgeIcon';
import isOverdue from '../utils/isOverdue';
import TaskRowShell from '../common/TaskRowShell';

import useDeepLinkAutoOpen from 'modules/common/hooks/useDeepLinkAutoOpen';
import MemberOperator, { useMemberOperatorTaskContext } from 'modules/common/components/MemberOperator';

function TaskRowContent({
    task,
    page,
    isCompleting,
    isDeepLinkTarget,
    autoOpenCta,
}: Readonly<{
    task: Task;
    page?: string;
    isCompleting?: boolean;
    isDeepLinkTarget?: boolean;
    autoOpenCta?: boolean;
}>) {
    const { onCtaClick, actionSurface } = useTaskSubmitAction(task, { autoOpen: autoOpenCta });
    const { sendTrackingEvent } = useMemberOperatorTaskContext();
    const { title, description } = getTaskDisplay(task);
    const overdue = isOverdue(task.due_date);

    const handleCtaClick = () => {
        // `page` is supplied by the caller (the /tasks page passes 'tasks'). Inside the group drawer
        // it's omitted — those opens are identified by the group context on trackingProperties instead.
        sendTrackingEvent('Task Viewed', { page, auto_opened: false });
        onCtaClick();
    };

    // A deep-link arrival (e.g. from a task comms email) auto-opens the CTA surface without a click,
    // so handleCtaClick never fires for it. Mirror useTaskSubmitAction's auto-open gating and timing
    // exactly, so a 'Task Viewed' is emitted iff the surface actually auto-opens.
    useDeepLinkAutoOpen(Boolean(autoOpenCta) && isOverlaySubmitAction(task.task_template.submit_action), () =>
        sendTrackingEvent('Task Viewed', { page, auto_opened: true })
    );

    return (
        <>
            <TaskRowShell
                title={title}
                description={description}
                onCtaClick={handleCtaClick}
                dateBadge={<DateBadge date={task.due_date} icon={priorityToDateBadgeIcon(task.priority)} />}
                overdue={overdue}
                isCompleting={isCompleting}
                isDeepLinkTarget={isDeepLinkTarget}
            />
            {actionSurface}
        </>
    );
}

export default function TaskRow({
    task,
    page,
    trackingProperties,
    isCompleting,
    isDeepLinkTarget,
    autoOpenCta,
}: Readonly<{
    task: Task;
    page?: string;
    trackingProperties?: Record<string, unknown>;
    // Injected by FeedItemTransition while this row plays its completion exit animation.
    isCompleting?: boolean;
    // Deep-link target: scroll + flash the row (both on the /tasks list and inside a group drawer).
    isDeepLinkTarget?: boolean;
    // Deep-link target on the /tasks list: also open its CTA surface once on mount (never a redirect).
    autoOpenCta?: boolean;
}>) {
    return (
        <MemberOperator
            task={task}
            trackViewOnMount={false}
            renderStatus={[...ACTIVE_TASK_STATUSES]}
            // Set when rendered inside a group drawer, so this row's events carry group context.
            trackingProperties={trackingProperties}>
            <TaskRowContent
                task={task}
                page={page}
                isCompleting={isCompleting}
                isDeepLinkTarget={isDeepLinkTarget}
                autoOpenCta={autoOpenCta}
            />
        </MemberOperator>
    );
}
